import { Button } from '@/atoms/Button';
import { SearchIcon } from '@/atoms/Icons';
import { useEnhancedRouter } from '@/hooks/useEnhancedRouter';
import { useTranslations } from '@/hooks/useTranslations';
import { fetchData } from '@/services/fetchData';
import { selectSearchActive, setSearchActive } from '@/store';
import { SearchResult, Variants } from '@/types';
import cn from 'classnames';
import debounce from 'lodash.debounce';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchItem } from '../SearchItem';
import { SearchNotFound } from '../SearchNotFound';
import styles from './Search.module.scss';

type SearchProps = {
  className?: string;
};

export function Search({ className }: SearchProps) {
  const searchActive = useSelector(selectSearchActive);

  const dispatch = useDispatch();
  const SearchRef = useRef<HTMLDivElement>(null);
  const InputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');

  const [itemsList, setItemsList] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const { push, locale } = useEnhancedRouter();
  const translations = useTranslations();

  const activateSearch = () => {
    dispatch(setSearchActive(true));
  };

  const clearSearch = () => {
    setValue('');
    setItemsList([]);
    setShowResults(false);
  };

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (SearchRef.current && !SearchRef.current.contains(target)) {
        dispatch(setSearchActive(false));
        setShowResults(false);
      }
    };

    window.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, [locale, dispatch]);

  useEffect(() => {
    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.code === 'Enter') {
        const response = await fetchData({
          url: `/search/autocomplete?q=${InputRef.current?.value}&lang=${locale}`,
        });

        if (response.code === 200) {
          setItemsList(response.list);
          setShowResults(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [locale]);

  const debouncedFetchAutoComplete = useRef(
    debounce(async (query: string) => {
      const response = await fetchData({
        url: `/search/autocomplete?q=${query}&lang=${locale}`,
      });
      if (response.code === 200) {
        setItemsList(response.list);
        setShowResults(true);
      }
    }, 1000),
  ).current;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowResults(false);
    const value = event.target.value;
    setValue(value);
    if (value.length < 2) return;

    debouncedFetchAutoComplete(value);
  };

  const wrapperClassName = cn(
    styles.wrapper,
    {
      [styles.wrapperActive]: searchActive,
    },
    className,
  );

  const searchClassName = cn(
    styles.search,
    {
      [styles.active]: searchActive,
    },
    className,
  );

  const viewAll = () => {
    push(`/search?q=${value}`);
  };

  return (
    <div className={wrapperClassName} onClick={activateSearch} ref={SearchRef}>
      <SearchIcon className={styles.icon} />
      <input
        type="text"
        className={searchClassName}
        value={value}
        onChange={handleChange}
        ref={InputRef}
      />
      <img
        loading="lazy"
        src="/svg/clear.svg"
        width={24}
        height={24}
        alt="clear"
        className={styles.clear}
        onClick={clearSearch}
      />
      {showResults && searchActive && value !== '' && (
        <div className={styles.results}>
          {itemsList.length < 1 ? (
            <SearchNotFound query={value} />
          ) : (
            <>
              {itemsList
                .map((item) => (
                  <SearchItem
                    key={item.id}
                    searchValue={value}
                    name={item.name}
                    id={item.id}
                    section={item.section}
                    score={item.score}
                    text={item.text}
                    url={item.url}
                  />
                ))
                .slice(0, 5)}
              <Button
                variant={Variants.Ghost}
                text={translations.searchAllResults}
                onClick={viewAll}
                className={styles.resultsButton}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
